import React, { ComponentProps, ReactNode, forwardRef, HTMLAttributes } from 'react';
import { p4Tokens } from '../../styles/themes/default/p4FluentTheme';
import { makeStyles, mergeClasses, Tooltip } from '@fluentui/react-components';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'center',
  },
  status: {
    display: 'flex',
    fontWeight: 600,
    padding: '2px 7px 2px 7px',
    marginLeft: '10px',
    fontSize: '12px',
    color: '#fff',
    backgroundColor: p4Tokens.statusFlagInfo,
    borderRadius: '2px',
  },
  successPill: {
    backgroundColor: p4Tokens.statusFlagSuccess,
  },
  warningPill: {
    backgroundColor: p4Tokens.statusFlagWarning,
  },
  infoPill: {
    backgroundColor: p4Tokens.statusFlagInfo,
  },
  basicPill: {
    backgroundColor: p4Tokens.statusFlagBasic,
  },
  dangerPill: {
    backgroundColor: p4Tokens.statusFlagDanger,
  },
  tooltip: {
    fontSize: '12px',
  },
});

/**
 * Pill/Status Flag common types
 */
export enum PillType {
  Success,
  Warning,
  Info,
  Basic,
  Danger,
}

export const PillContainer = ({ className, children }: { className?: string; children: ReactNode }) => {
  const styles = useStyles();

  return <div className={mergeClasses(styles.container, className)}>{children}</div>;
};

export interface PillProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  type?: PillType;
  tooltip?: ComponentProps<typeof Tooltip>['content'];
}

/**
 * Standard Pill Components for creating status flag/pill like elements in P4.
 *
 * If more than one pill is to be displayed at once, wrap in a PillContainer element, i.e.
 * <PillContainer>
 *   <Pill>One</Pill>
 *   <Pill>Two</Pill>
 * </PillContainer>
 *
 * TODO - Merge with HeaderStatus (Ideally make HeaderStatus use 'Pill') - Wait for release/12 to avoid unnecessary conflicts.
 *
 * @param children - Content of the pill, i.e. 'Allocated'
 * @param type - The pill type (Drives the background colour, if no override is provided).
 * @param className - Override the class of the pill, such as to provide a custom colour in exceptional circumstances.
 * @param tooltip - Display a tooltip when hovering over the Pill, showing this content.
 */
export const Pill = forwardRef<HTMLDivElement, PillProps>(
  ({ children, type = PillType.Info, className, tooltip, ...otherProps }, ref) => {
    const styles = useStyles();

    let statusClassName: string;
    switch (type) {
      case PillType.Success:
        statusClassName = styles.successPill;
        break;
      case PillType.Warning:
        statusClassName = styles.warningPill;
        break;
      case PillType.Info:
      default:
        statusClassName = styles.infoPill;
        break;
      case PillType.Basic:
        statusClassName = styles.basicPill;
        break;
      case PillType.Danger:
        statusClassName = styles.dangerPill;
        break;
    }

    const content = (
      <div ref={ref} className={mergeClasses(styles.status, statusClassName, className)} {...otherProps}>
        {children}
      </div>
    );

    if (tooltip) {
      return (
        <Tooltip content={tooltip} relationship={'label'} withArrow={true}>
          {content}
        </Tooltip>
      );
    }
    return content;
  }
);
